/**
 * Toggle Component for Poker Reborn
 *
 * A toggle switch for boolean settings (on/off).
 *
 * @see docs/screens/settings/README.md - Used in Settings screen
 */

import { Container, Graphics, Text } from 'pixi.js';

// =============================================================================
// TYPES
// =============================================================================

export interface ToggleStyle {
  /** Track color when off */
  trackOffColor: number;

  /** Track color when on */
  trackOnColor: number;

  /** Thumb color */
  thumbColor: number;

  /** Track width */
  trackWidth: number;

  /** Track height */
  trackHeight: number;

  /** Thumb size */
  thumbSize: number;

  /** Label color */
  labelColor: number;

  /** Font size */
  fontSize: number;

  /** Font family */
  fontFamily: string;
}

export interface ToggleOptions {
  /** Current value */
  value?: boolean;

  /** Label text */
  label?: string;

  /** Custom style overrides */
  style?: Partial<ToggleStyle>;
}

// =============================================================================
// DEFAULT STYLE
// =============================================================================

const DEFAULT_STYLE: ToggleStyle = {
  trackOffColor: 0x444455,
  trackOnColor: 0x4a8a44,
  thumbColor: 0xffffff,
  trackWidth: 50,
  trackHeight: 26,
  thumbSize: 22,
  labelColor: 0xcccccc,
  fontSize: 14,
  fontFamily: 'Roboto, Arial, sans-serif',
};

// =============================================================================
// TOGGLE COMPONENT
// =============================================================================

export class Toggle extends Container {
  private track: Graphics;
  private thumb: Graphics;
  private labelText: Text | null = null;

  private style: ToggleStyle;
  private _value: boolean;

  /** Callback when value changes */
  public onChange?: (value: boolean) => void;

  constructor(options: ToggleOptions = {}) {
    super();

    this.style = { ...DEFAULT_STYLE, ...options.style };
    this._value = options.value ?? false;

    // Create label if provided
    if (options.label) {
      this.labelText = new Text({
        text: options.label,
        style: {
          fill: this.style.labelColor,
          fontSize: this.style.fontSize,
          fontFamily: this.style.fontFamily,
        },
      });
      this.addChild(this.labelText);
    }

    // Create track
    this.track = new Graphics();
    this.track.eventMode = 'static';
    this.track.cursor = 'pointer';
    this.track.on('pointerdown', this.toggle, this);
    this.addChild(this.track);

    // Create thumb
    this.thumb = new Graphics();
    this.addChild(this.thumb);

    // Initial draw
    this.draw();
  }

  /** Get current value */
  get value(): boolean {
    return this._value;
  }

  /** Set current value */
  set value(newValue: boolean) {
    if (newValue !== this._value) {
      this._value = newValue;
      this.draw();
      this.onChange?.(this._value);
    }
  }

  /** Toggle the value */
  toggle(): void {
    this.value = !this._value;
  }

  /** Get the total width of the toggle (label + track) */
  getWidth(): number {
    if (this.labelText) {
      return this.labelText.width + 12 + this.style.trackWidth;
    }
    return this.style.trackWidth;
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private draw(): void {
    const trackX = this.labelText ? this.labelText.width + 12 : 0;
    const trackY = this.labelText
      ? (this.labelText.height - this.style.trackHeight) / 2
      : 0;

    // Draw track
    const trackColor = this._value
      ? this.style.trackOnColor
      : this.style.trackOffColor;

    this.track.clear();
    this.track.roundRect(
      trackX,
      trackY,
      this.style.trackWidth,
      this.style.trackHeight,
      this.style.trackHeight / 2
    );
    this.track.fill(trackColor);

    // Draw thumb
    const thumbPadding = (this.style.trackHeight - this.style.thumbSize) / 2;
    const thumbX = this._value
      ? trackX + this.style.trackWidth - this.style.thumbSize - thumbPadding
      : trackX + thumbPadding;
    const thumbY = trackY + thumbPadding;

    this.thumb.clear();
    this.thumb.roundRect(
      thumbX,
      thumbY,
      this.style.thumbSize,
      this.style.thumbSize,
      this.style.thumbSize / 2
    );
    this.thumb.fill(this.style.thumbColor);
  }
}
